/**
 * Gallery Dialogs & Floating Banners
 *
 * Extracted from ProductGalleryWidget for file size compliance.
 * Contains: Gallery Actions Modal, all floating mode banners, video lightbox.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Image as ImageIcon, Star, Trash2, ArrowUpDown, Video, X, Award } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface GalleryLabels {
  galleryActionsTitle: string;
  galleryActionsDescription: string;
  addImages: string;
  addImagesDescription: string;
  choosePrimaryImage: string;
  doneChoosingPrimary: string;
  choosePrimaryImageDescription: string;
  exitPrimarySelectionDescription: string;
  rearrangeImages: string;
  doneRearranging: string;
  rearrangeImagesDescription: string;
  exitRearrangeModeDescription: string;
  addVideos: string;
  maxVideosAllowed: string;
  uploadVideosToGallery: string;
  choosePrimaryVideo: string;
  doneChoosingPrimaryVideo: string;
  choosePrimaryVideoDescription: string;
  exitPrimaryVideoSelectionDescription: string;
  removeMedia: string;
  selectMediaToRemove: string;
  done: string;
  cancel: string;
  delete: string;
  removing: string;
  bannerChoosePrimaryImage: string;
  bannerChoosePrimaryImageHint: string;
  bannerChoosePrimaryVideo: string;
  bannerChoosePrimaryVideoHint: string;
  bannerRearrangeTitle: string;
  bannerRearrangeHint: string;
  selectImagesToRemove: string;
  tapImagesToSelect: string;
  imageSingular: string;
  imagePlural: string;
  selected: string;
  confirmRemoveFromGalleryTemplate: string;
  noProductImagesTitle: string;
  noProductImagesSubtitle: string;
  adding: string;
}

// ── Gallery Actions Modal ─────────────────────────────────────────────────────

interface GalleryActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels: GalleryLabels;
  hasImages: boolean;
  videoCount: number;
  disabled?: boolean;
  itemCode: string;
  isDraftMode: boolean;
  isChoosingPrimary: boolean;
  isChoosingPrimaryVideo: boolean;
  isRearranging: boolean;
  isSaving: boolean;
  isAddingImages: boolean;
  isAddingVideos: boolean;
  onAddImages: () => void;
  onToggleChoosePrimary: () => void;
  onToggleRearrange: () => void;
  onAddVideos: () => void;
  onToggleChoosePrimaryVideo: () => void;
  onEnterDeleteMode: () => void;
}

export function GalleryActionsDialog({
  open,
  onOpenChange,
  labels,
  hasImages,
  videoCount,
  disabled,
  itemCode,
  isDraftMode,
  isChoosingPrimary,
  isChoosingPrimaryVideo,
  isRearranging,
  isSaving,
  isAddingImages,
  isAddingVideos,
  onAddImages,
  onToggleChoosePrimary,
  onToggleRearrange,
  onAddVideos,
  onToggleChoosePrimaryVideo,
  onEnterDeleteMode,
}: GalleryActionsDialogProps) {
  const actionBtnClass = cn(
    'w-full flex items-center gap-5 min-h-[60px] px-6 py-4',
    'hover:bg-gray-100/80 dark:hover:bg-gray-900/60 transition-all duration-200 ease-out',
    'active:bg-gray-100 dark:active:bg-gray-900/70',
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:active:bg-transparent',
  );
  const borderClass = 'border-b border-gray-200/50 dark:border-gray-700/50';
  const groupClass = 'bg-gray-50 dark:bg-gray-950 rounded-lg overflow-hidden border border-gray-200/50 dark:border-gray-700/50 shadow-sm';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[680px] max-h-[85vh] bg-white dark:bg-gray-900 p-0 flex flex-col overflow-hidden">
        <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
        <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
          <DialogHeader className="px-10 pt-20 pb-4 text-center">
            <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px] mb-2">
              {labels.galleryActionsTitle}
            </DialogTitle>
            <DialogDescription className="text-[15px] font-normal text-gray-600 dark:text-gray-400 leading-relaxed">
              {labels.galleryActionsDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="pb-10 px-10 pt-4">
            <div className="space-y-6">
              {/* Main Actions Group */}
              <div className={groupClass}>
                {/* Add Images */}
                <button
                  type="button"
                  onClick={onAddImages}
                  disabled={disabled || (!itemCode && !isDraftMode) || isAddingImages}
                  className={cn(actionBtnClass, borderClass)}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                      {labels.addImages}
                    </div>
                    <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                      {labels.addImagesDescription}
                    </div>
                  </div>
                </button>

                {/* Choose Primary Image */}
                {hasImages && (
                  <button
                    type="button"
                    onClick={onToggleChoosePrimary}
                    disabled={disabled}
                    className={cn(actionBtnClass, borderClass)}
                  >
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <Star className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                        {isChoosingPrimary ? labels.doneChoosingPrimary : labels.choosePrimaryImage}
                      </div>
                      <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                        {isChoosingPrimary ? labels.exitPrimarySelectionDescription : labels.choosePrimaryImageDescription}
                      </div>
                    </div>
                  </button>
                )}

                {/* Rearrange */}
                {hasImages && (
                  <button
                    type="button"
                    onClick={onToggleRearrange}
                    disabled={disabled || isSaving}
                    className={cn(actionBtnClass)}
                  >
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <ArrowUpDown className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                        {isRearranging ? labels.doneRearranging : labels.rearrangeImages}
                      </div>
                      <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                        {isRearranging ? labels.exitRearrangeModeDescription : labels.rearrangeImagesDescription}
                      </div>
                    </div>
                  </button>
                )}
              </div>

              {/* Video Actions Group */}
              <div className={groupClass}>
                {/* Add Videos */}
                <button
                  type="button"
                  onClick={onAddVideos}
                  disabled={disabled || !itemCode || isAddingVideos || videoCount >= 3}
                  className={cn(actionBtnClass, videoCount > 0 && borderClass)}
                >
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <Video className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                      {labels.addVideos}
                    </div>
                    <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                      {videoCount >= 3 ? labels.maxVideosAllowed : `${labels.uploadVideosToGallery} (${videoCount}/3)`}
                    </div>
                  </div>
                </button>

                {/* Choose Primary Video */}
                {videoCount > 0 && (
                  <button
                    type="button"
                    onClick={onToggleChoosePrimaryVideo}
                    disabled={disabled}
                    className={cn(actionBtnClass)}
                  >
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <Award className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[17px] font-normal text-gray-900 dark:text-gray-100 leading-[1.35]">
                        {isChoosingPrimaryVideo ? labels.doneChoosingPrimaryVideo : labels.choosePrimaryVideo}
                      </div>
                      <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                        {isChoosingPrimaryVideo ? labels.exitPrimaryVideoSelectionDescription : labels.choosePrimaryVideoDescription}
                      </div>
                    </div>
                  </button>
                )}
              </div>

              {/* Remove Media Group */}
              {hasImages && (
                <div className={groupClass}>
                  <button
                    type="button"
                    onClick={onEnterDeleteMode}
                    disabled={disabled || isSaving}
                    className={cn(actionBtnClass)}
                  >
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-[17px] font-normal text-red-600 dark:text-red-400 leading-[1.35]">
                        {labels.removeMedia}
                      </div>
                      <div className="text-[15px] text-gray-500 dark:text-gray-400 mt-1 leading-[1.4]">
                        {labels.selectMediaToRemove}
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Floating Mode Banners ─────────────────────────────────────────────────────

interface GalleryFloatingBannersProps {
  labels: GalleryLabels;
  isDeleteMode: boolean;
  isRearranging: boolean;
  isChoosingPrimary: boolean;
  isChoosingPrimaryVideo: boolean;
  selectedCount: number;
  isSaving: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onToggleRearrange: () => void;
  onDoneChoosingPrimary: () => void;
  onDoneChoosingPrimaryVideo: () => void;
}

const bannerBase = 'fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-xl border border-gray-700/50 dark:border-gray-600/30 shadow-lg rounded-lg px-5 py-4 min-w-[340px] max-w-[90vw] safe-area-inset-bottom animate-in slide-in-from-bottom-4 duration-300';

export function GalleryFloatingBanners({
  labels,
  isDeleteMode,
  isRearranging,
  isChoosingPrimary,
  isChoosingPrimaryVideo,
  selectedCount,
  isSaving,
  onCancelDelete,
  onConfirmDelete,
  onToggleRearrange,
  onDoneChoosingPrimary,
  onDoneChoosingPrimaryVideo,
}: GalleryFloatingBannersProps) {
  return (
    <>
      {/* Delete Mode */}
      {isDeleteMode && (
        <div className={bannerBase}>
          <div className="flex items-center justify-between gap-5">
            <div className="flex items-center gap-3.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-destructive/20 dark:bg-destructive/30">
                <Trash2 className="h-4.5 w-4.5 text-destructive-foreground" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold text-white leading-tight">
                  {selectedCount > 0
                    ? `${selectedCount} ${selectedCount > 1 ? labels.imagePlural : labels.imageSingular} ${labels.selected}`
                    : labels.selectImagesToRemove}
                </span>
                {selectedCount === 0 && (
                  <span className="text-[13px] text-gray-300 leading-tight">{labels.tapImagesToSelect}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancelDelete}
                disabled={isSaving}
                className="text-[14px] font-medium px-4 py-2 h-9 rounded text-white hover:bg-white/10 hover:text-white"
              >
                {labels.cancel}
              </Button>
              {selectedCount > 0 && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={onConfirmDelete}
                  disabled={isSaving || selectedCount === 0}
                  className="text-[14px] font-medium px-5 py-2 h-9 rounded min-w-[90px] bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                >
                  {isSaving ? labels.removing : `${labels.delete} ${selectedCount}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rearrange Mode */}
      {isRearranging && (
        <div className={bannerBase}>
          <div className="flex items-center justify-between gap-5">
            <div className="flex items-center gap-3.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/20 dark:bg-primary/30">
                <ArrowUpDown className="h-4.5 w-4.5 text-primary-foreground" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold text-white leading-tight">
                  {labels.bannerRearrangeTitle}
                </span>
                <span className="text-[13px] text-gray-300 leading-tight">{labels.bannerRearrangeHint}</span>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onToggleRearrange}
              className="text-[14px] font-medium px-5 py-2 h-9 rounded text-white hover:bg-white/10 hover:text-white"
            >
              {labels.done}
            </Button>
          </div>
        </div>
      )}

      {/* Choose Primary Image Mode */}
      {isChoosingPrimary && (
        <div className={bannerBase}>
          <div className="flex items-center justify-between gap-5">
            <div className="flex items-center gap-3.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/20 dark:bg-primary/30">
                <Star className="h-4.5 w-4.5 text-primary-foreground" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold text-white leading-tight">
                  {labels.bannerChoosePrimaryImage}
                </span>
                <span className="text-[13px] text-gray-300 leading-tight">{labels.bannerChoosePrimaryImageHint}</span>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDoneChoosingPrimary}
              className="text-[14px] font-medium px-5 py-2 h-9 rounded text-white hover:bg-white/10 hover:text-white"
            >
              {labels.done}
            </Button>
          </div>
        </div>
      )}

      {/* Choose Primary Video Mode */}
      {isChoosingPrimaryVideo && (
        <div className={bannerBase}>
          <div className="flex items-center justify-between gap-5">
            <div className="flex items-center gap-3.5">
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/20 dark:bg-primary/30">
                <Video className="h-4.5 w-4.5 text-primary-foreground" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold text-white leading-tight">
                  {labels.bannerChoosePrimaryVideo}
                </span>
                <span className="text-[13px] text-gray-300 leading-tight">{labels.bannerChoosePrimaryVideoHint}</span>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDoneChoosingPrimaryVideo}
              className="text-[14px] font-medium px-5 py-2 h-9 rounded text-white hover:bg-white/10 hover:text-white"
            >
              {labels.done}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Video Lightbox ────────────────────────────────────────────────────────────

interface VideoLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoUrl: string | null;
}

export function VideoLightbox({ open, onOpenChange, videoUrl }: VideoLightboxProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-full p-0 bg-black/95 backdrop-blur-xl border-0" aria-describedby={undefined}>
        <VisuallyHidden>
          <DialogTitle>Video Player</DialogTitle>
        </VisuallyHidden>
        {videoUrl && (
          <div className="relative w-full aspect-video bg-black">
            <video
              src={videoUrl}
              controls
              autoPlay
              className="w-full h-full object-contain"
              onEnded={() => onOpenChange(false)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="absolute top-4 right-4 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full w-10 h-10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
